import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';

/**
 * Utility functions for exporting Supabase data to Excel files
 */

// Helper function to download a file
const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to format date for filename
const getDateString = () => {
  return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
};

/**
 * Export all products to Excel
 */
export const exportProducts = async () => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('group', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data || []);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Products');

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    downloadFile(blob, `products_${getDateString()}.xlsx`);
    return { success: true, message: 'Products exported successfully!' };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export all questions with product information to Excel
 */
export const exportQuestions = async () => {
  try {
    const { data, error } = await supabase
      .from('questions')
      .select(`
        id,
        key,
        label,
        type,
        required,
        options_json,
        validation_json,
        sort_order,
        products!inner(slug, name, group)
      `)
      .order('products.group', { ascending: true })
      .order('products.name', { ascending: true })
      .order('sort_order', { ascending: true });

    if (error) throw error;

    // Flatten the data for Excel export
    const flattenedData = data?.map(question => ({
      question_id: question.id,
      question_key: question.key,
      question_label: question.label,
      question_type: question.type,
      required: question.required,
      sort_order: question.sort_order,
      product_slug: question.products?.slug,
      product_name: question.products?.name,
      product_group: question.products?.group,
      options: question.options_json ? JSON.stringify(question.options_json) : '',
      validation: question.validation_json ? JSON.stringify(question.validation_json) : ''
    })) || [];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(flattenedData);

    // Auto-size columns
    const colWidths = [
      { wch: 15 }, // question_id
      { wch: 20 }, // question_key
      { wch: 40 }, // question_label
      { wch: 15 }, // question_type
      { wch: 10 }, // required
      { wch: 10 }, // sort_order
      { wch: 20 }, // product_slug
      { wch: 30 }, // product_name
      { wch: 20 }, // product_group
      { wch: 50 }, // options
      { wch: 30 }  // validation
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Questions');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    downloadFile(blob, `questions_${getDateString()}.xlsx`);
    return { success: true, message: 'Questions exported successfully!' };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export all leads with contact information to Excel
 */
export const exportLeads = async () => {
  try {
    const { data, error } = await supabase
      .from('leads')
      .select(`
        *,
        lead_contacts(*)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Flatten leads with contact information
    const flattenedData = data?.map(lead => ({
      lead_id: lead.id,
      licence_location: lead.licence_location,
      physical_property: lead.physical_property,
      client_visits: lead.client_visits,
      cyber_concerns: lead.cyber_concerns,
      professional_advice: lead.professional_advice,
      recommended_products: lead.recommended_products?.join(', ') || '',
      created_at: lead.created_at,
      contact_name: lead.lead_contacts?.[0]?.name || '',
      contact_email: lead.lead_contacts?.[0]?.email || '',
      contact_phone: lead.lead_contacts?.[0]?.phone || ''
    })) || [];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(flattenedData);

    // Auto-size columns
    const colWidths = [
      { wch: 15 }, // lead_id
      { wch: 20 }, // licence_location
      { wch: 20 }, // physical_property
      { wch: 15 }, // client_visits
      { wch: 15 }, // cyber_concerns
      { wch: 20 }, // professional_advice
      { wch: 40 }, // recommended_products
      { wch: 20 }, // created_at
      { wch: 25 }, // contact_name
      { wch: 30 }, // contact_email
      { wch: 20 }  // contact_phone
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Leads');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    downloadFile(blob, `leads_${getDateString()}.xlsx`);
    return { success: true, message: 'Leads exported successfully!' };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export all submissions with answers to Excel
 */
export const exportSubmissions = async () => {
  try {
    const { data, error } = await supabase
      .from('submissions')
      .select(`
        *,
        leads!inner(id, created_at, lead_contacts(name, email, phone)),
        products!inner(slug, name, group)
      `)
      .order('submitted_at', { ascending: false });

    if (error) throw error;

    // Flatten submissions data
    const flattenedData = data?.map(submission => ({
      submission_id: submission.id,
      lead_id: submission.lead_id,
      product_slug: submission.products?.slug,
      product_name: submission.products?.name,
      product_group: submission.products?.group,
      submitted_at: submission.submitted_at,
      lead_created_at: submission.leads?.created_at,
      contact_name: submission.leads?.lead_contacts?.[0]?.name || '',
      contact_email: submission.leads?.lead_contacts?.[0]?.email || '',
      contact_phone: submission.leads?.lead_contacts?.[0]?.phone || '',
      answers: JSON.stringify(submission.answers_json)
    })) || [];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(flattenedData);

    // Auto-size columns
    const colWidths = [
      { wch: 15 }, // submission_id
      { wch: 15 }, // lead_id
      { wch: 20 }, // product_slug
      { wch: 30 }, // product_name
      { wch: 20 }, // product_group
      { wch: 20 }, // submitted_at
      { wch: 20 }, // lead_created_at
      { wch: 25 }, // contact_name
      { wch: 30 }, // contact_email
      { wch: 20 }, // contact_phone
      { wch: 100 } // answers
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Submissions');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    downloadFile(blob, `submissions_${getDateString()}.xlsx`);
    return { success: true, message: 'Submissions exported successfully!' };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export all uploaded files metadata to Excel
 */
export const exportUploads = async () => {
  try {
    const { data, error } = await supabase
      .from('uploads')
      .select(`
        *,
        submissions!inner(
          id,
          leads!inner(
            id,
            lead_contacts(name, email, phone)
          ),
          products!inner(slug, name, group)
        )
      `)
      .order('uploaded_at', { ascending: false });

    if (error) throw error;

    // Flatten uploads data
    const flattenedData = data?.map(upload => ({
      upload_id: upload.id,
      submission_id: upload.submission_id,
      file_path: upload.file_path,
      doc_type: upload.doc_type,
      mime_type: upload.mime_type,
      size_bytes: upload.size_bytes,
      size_formatted: upload.size_bytes ? `${Math.round(upload.size_bytes / 1024)} KB` : '',
      uploaded_at: upload.uploaded_at,
      lead_id: upload.submissions?.leads?.id,
      contact_name: upload.submissions?.leads?.lead_contacts?.[0]?.name || '',
      contact_email: upload.submissions?.leads?.lead_contacts?.[0]?.email || '',
      product_slug: upload.submissions?.products?.slug,
      product_name: upload.submissions?.products?.name,
      product_group: upload.submissions?.products?.group
    })) || [];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(flattenedData);

    // Auto-size columns
    const colWidths = [
      { wch: 15 }, // upload_id
      { wch: 15 }, // submission_id
      { wch: 40 }, // file_path
      { wch: 15 }, // doc_type
      { wch: 25 }, // mime_type
      { wch: 12 }, // size_bytes
      { wch: 12 }, // size_formatted
      { wch: 20 }, // uploaded_at
      { wch: 15 }, // lead_id
      { wch: 25 }, // contact_name
      { wch: 30 }, // contact_email
      { wch: 20 }, // product_slug
      { wch: 30 }, // product_name
      { wch: 20 }  // product_group
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'File Uploads');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    downloadFile(blob, `file_uploads_${getDateString()}.xlsx`);
    return { success: true, message: 'File uploads exported successfully!' };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export comprehensive report with all data in multiple sheets
 */
export const exportComprehensiveReport = async () => {
  try {
    // Fetch all data
    const [productsResult, questionsResult, leadsResult, submissionsResult] = await Promise.all([
      supabase.from('products').select('*').order('group', { ascending: true }),
      supabase.from('questions').select(`
        id, key, label, type, required, options_json, validation_json, sort_order,
        products!inner(slug, name, group)
      `).order('products.group', { ascending: true }),
      supabase.from('leads').select(`
        *, lead_contacts(*)
      `).order('created_at', { ascending: false }),
      supabase.from('submissions').select(`
        *, leads!inner(id, created_at, lead_contacts(name, email, phone)),
        products!inner(slug, name, group)
      `).order('submitted_at', { ascending: false })
    ]);

    // Check for errors
    if (productsResult.error) throw productsResult.error;
    if (questionsResult.error) throw questionsResult.error;
    if (leadsResult.error) throw leadsResult.error;
    if (submissionsResult.error) throw submissionsResult.error;

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add Products sheet
    const productsWs = XLSX.utils.json_to_sheet(productsResult.data || []);
    XLSX.utils.book_append_sheet(wb, productsWs, 'Products');

    // Add Questions sheet (flattened)
    const questionsFlat = questionsResult.data?.map(q => ({
      question_id: q.id,
      key: q.key,
      label: q.label,
      type: q.type,
      required: q.required,
      sort_order: q.sort_order,
      product_slug: q.products?.slug,
      product_name: q.products?.name,
      product_group: q.products?.group,
      options: q.options_json ? JSON.stringify(q.options_json) : '',
      validation: q.validation_json ? JSON.stringify(q.validation_json) : ''
    })) || [];
    const questionsWs = XLSX.utils.json_to_sheet(questionsFlat);
    XLSX.utils.book_append_sheet(wb, questionsWs, 'Questions');

    // Add Leads sheet (flattened)
    const leadsFlat = leadsResult.data?.map(lead => ({
      lead_id: lead.id,
      licence_location: lead.licence_location,
      physical_property: lead.physical_property,
      client_visits: lead.client_visits,
      cyber_concerns: lead.cyber_concerns,
      professional_advice: lead.professional_advice,
      recommended_products: lead.recommended_products?.join(', ') || '',
      created_at: lead.created_at,
      contact_name: lead.lead_contacts?.[0]?.name || '',
      contact_email: lead.lead_contacts?.[0]?.email || '',
      contact_phone: lead.lead_contacts?.[0]?.phone || ''
    })) || [];
    const leadsWs = XLSX.utils.json_to_sheet(leadsFlat);
    XLSX.utils.book_append_sheet(wb, leadsWs, 'Leads');

    // Add Submissions sheet (flattened)
    const submissionsFlat = submissionsResult.data?.map(submission => ({
      submission_id: submission.id,
      lead_id: submission.leads?.id,
      product_slug: submission.products?.slug,
      product_name: submission.products?.name,
      submitted_at: submission.submitted_at,
      contact_name: submission.leads?.lead_contacts?.[0]?.name || '',
      contact_email: submission.leads?.lead_contacts?.[0]?.email || '',
      answers: JSON.stringify(submission.answers_json)
    })) || [];
    const submissionsWs = XLSX.utils.json_to_sheet(submissionsFlat);
    XLSX.utils.book_append_sheet(wb, submissionsWs, 'Submissions');

    // Generate and download
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    downloadFile(blob, `business_insurance_report_${getDateString()}.xlsx`);
    return { success: true, message: 'Comprehensive report exported successfully!' };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Tenant-specific export functions

interface TenantExportFilters {
  dateFrom?: Date;
  dateTo?: Date;
  searchTerm?: string;
}

/**
 * Export tenant-specific leads with filtering
 */
export const exportTenantLeads = async (tenantId: string, filters: TenantExportFilters = {}) => {
  try {
    let query = supabase
      .from('leads')
      .select(`
        id,
        licence_location,
        physical_property,
        client_visits,
        cyber_concerns,
        professional_advice,
        recommended_products,
        created_at,
        lead_contacts(
          id,
          name,
          email,
          phone
        )
      `)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    // Apply date filters
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom.toISOString());
    }
    if (filters.dateTo) {
      // Add one day to include the entire end date
      const endDate = new Date(filters.dateTo);
      endDate.setDate(endDate.getDate() + 1);
      query = query.lt('created_at', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) throw error;

    // Flatten the data for Excel export
    const flattenedData = (data || []).map(lead => {
      const contact = lead.lead_contacts?.[0] || {};
      return {
        lead_id: lead.id,
        contact_name: contact.name || 'N/A',
        contact_email: contact.email || 'N/A',
        contact_phone: contact.phone || 'N/A',
        licence_location: lead.licence_location || 'N/A',
        physical_property: lead.physical_property || 'N/A',
        client_visits: lead.client_visits || 'N/A',
        cyber_concerns: lead.cyber_concerns || 'N/A',
        professional_advice: lead.professional_advice || 'N/A',
        recommended_products: Array.isArray(lead.recommended_products)
          ? lead.recommended_products.join(', ')
          : 'N/A',
        created_at: lead.created_at ? new Date(lead.created_at).toLocaleString() : 'N/A'
      };
    });

    // Filter by search term if provided
    let filteredData = flattenedData;
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = flattenedData.filter(lead =>
        lead.contact_name.toLowerCase().includes(searchLower) ||
        lead.contact_email.toLowerCase().includes(searchLower) ||
        lead.contact_phone.toLowerCase().includes(searchLower)
      );
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);

    // Auto-size columns
    const colWidths = [
      { wch: 15 }, // lead_id
      { wch: 25 }, // contact_name
      { wch: 30 }, // contact_email
      { wch: 20 }, // contact_phone
      { wch: 20 }, // licence_location
      { wch: 20 }, // physical_property
      { wch: 15 }, // client_visits
      { wch: 15 }, // cyber_concerns
      { wch: 20 }, // professional_advice
      { wch: 40 }, // recommended_products
      { wch: 20 }  // created_at
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Tenant Leads');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const dateStr = getDateString();
    downloadFile(blob, `tenant_leads_${dateStr}.xlsx`);
    return { success: true, message: `${filteredData.length} leads exported successfully!` };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export tenant-specific submissions with filtering
 */
export const exportTenantSubmissions = async (tenantId: string, filters: TenantExportFilters = {}) => {
  try {
    let query = supabase
      .from('submissions')
      .select(`
        id,
        answers_json,
        submitted_at,
        leads!inner(
          id,
          created_at,
          lead_contacts(name, email, phone)
        ),
        products!inner(slug, name, group)
      `)
      .eq('tenant_id', tenantId)
      .order('submitted_at', { ascending: false });

    // Apply date filters
    if (filters.dateFrom) {
      query = query.gte('submitted_at', filters.dateFrom.toISOString());
    }
    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo);
      endDate.setDate(endDate.getDate() + 1);
      query = query.lt('submitted_at', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) throw error;

    // Flatten the data for Excel export
    const flattenedData = (data || []).map(submission => {
      const contact = submission.leads?.lead_contacts?.[0] || {};
      const answers = submission.answers_json || {};

      return {
        submission_id: submission.id,
        lead_id: submission.leads?.id || 'N/A',
        contact_name: contact.name || 'N/A',
        contact_email: contact.email || 'N/A',
        contact_phone: contact.phone || 'N/A',
        product_slug: submission.products?.slug || 'N/A',
        product_name: submission.products?.name || 'N/A',
        product_group: submission.products?.group || 'N/A',
        answers: typeof answers === 'object' ? JSON.stringify(answers) : String(answers),
        submitted_at: submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : 'N/A',
        lead_created_at: submission.leads?.created_at ? new Date(submission.leads.created_at).toLocaleString() : 'N/A'
      };
    });

    // Filter by search term if provided
    let filteredData = flattenedData;
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = flattenedData.filter(submission =>
        submission.contact_name.toLowerCase().includes(searchLower) ||
        submission.contact_email.toLowerCase().includes(searchLower) ||
        submission.product_name.toLowerCase().includes(searchLower)
      );
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);

    // Auto-size columns
    const colWidths = [
      { wch: 15 }, // submission_id
      { wch: 15 }, // lead_id
      { wch: 25 }, // contact_name
      { wch: 30 }, // contact_email
      { wch: 20 }, // contact_phone
      { wch: 20 }, // product_slug
      { wch: 30 }, // product_name
      { wch: 20 }, // product_group
      { wch: 50 }, // answers
      { wch: 20 }, // submitted_at
      { wch: 20 }  // lead_created_at
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Tenant Submissions');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const dateStr = getDateString();
    downloadFile(blob, `tenant_submissions_${dateStr}.xlsx`);
    return { success: true, message: `${filteredData.length} submissions exported successfully!` };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Export comprehensive tenant report with all data in multiple sheets
 */
export const exportTenantComprehensive = async (tenantId: string, filters: TenantExportFilters = {}) => {
  try {
    // Build date filter conditions
    let dateFilter = '';
    const params: (string | Date)[] = [tenantId];

    if (filters.dateFrom || filters.dateTo) {
      if (filters.dateFrom) {
        dateFilter += ' AND leads.created_at >= $2';
        params.push(filters.dateFrom.toISOString());
      }
      if (filters.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setDate(endDate.getDate() + 1);
        dateFilter += ` AND leads.created_at < $${params.length + 1}`;
        params.push(endDate.toISOString());
      }
    }

    // Fetch tenant-specific data
    const [leadsResult, submissionsResult, productsResult] = await Promise.all([
      // Leads with contacts
      supabase
        .from('leads')
        .select(`
          id,
          licence_location,
          physical_property,
          client_visits,
          cyber_concerns,
          professional_advice,
          recommended_products,
          created_at,
          lead_contacts(id, name, email, phone)
        `)
        .eq('tenant_id', tenantId)
        .gte('created_at', filters.dateFrom?.toISOString() || '1900-01-01')
        .lt('created_at', filters.dateTo ? (() => {
          const endDate = new Date(filters.dateTo);
          endDate.setDate(endDate.getDate() + 1);
          return endDate.toISOString();
        })() : '2100-01-01')
        .order('created_at', { ascending: false }),

      // Submissions
      supabase
        .from('submissions')
        .select(`
          id,
          answers_json,
          submitted_at,
          leads!inner(
            id,
            created_at,
            lead_contacts(name, email, phone)
          ),
          products!inner(slug, name, group)
        `)
        .eq('tenant_id', tenantId)
        .gte('submitted_at', filters.dateFrom?.toISOString() || '1900-01-01')
        .lt('submitted_at', filters.dateTo ? (() => {
          const endDate = new Date(filters.dateTo);
          endDate.setDate(endDate.getDate() + 1);
          return endDate.toISOString();
        })() : '2100-01-01')
        .order('submitted_at', { ascending: false }),

      // Tenant products
      supabase
        .from('products')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('group', { ascending: true })
    ]);

    // Check for errors
    if (leadsResult.error) throw leadsResult.error;
    if (submissionsResult.error) throw submissionsResult.error;
    if (productsResult.error) throw productsResult.error;

    // Process leads data
    const leadsData = (leadsResult.data || []).map(lead => {
      const contact = lead.lead_contacts?.[0] || {};
      return {
        lead_id: lead.id,
        contact_name: contact.name || 'N/A',
        contact_email: contact.email || 'N/A',
        contact_phone: contact.phone || 'N/A',
        licence_location: lead.licence_location || 'N/A',
        physical_property: lead.physical_property || 'N/A',
        client_visits: lead.client_visits || 'N/A',
        cyber_concerns: lead.cyber_concerns || 'N/A',
        professional_advice: lead.professional_advice || 'N/A',
        recommended_products: Array.isArray(lead.recommended_products)
          ? lead.recommended_products.join(', ')
          : 'N/A',
        created_at: lead.created_at ? new Date(lead.created_at).toLocaleString() : 'N/A'
      };
    });

    // Process submissions data
    const submissionsData = (submissionsResult.data || []).map(submission => {
      const contact = submission.leads?.lead_contacts?.[0] || {};
      const answers = submission.answers_json || {};

      return {
        submission_id: submission.id,
        lead_id: submission.leads?.id || 'N/A',
        contact_name: contact.name || 'N/A',
        contact_email: contact.email || 'N/A',
        contact_phone: contact.phone || 'N/A',
        product_slug: submission.products?.slug || 'N/A',
        product_name: submission.products?.name || 'N/A',
        product_group: submission.products?.group || 'N/A',
        answers: typeof answers === 'object' ? JSON.stringify(answers) : String(answers),
        submitted_at: submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : 'N/A'
      };
    });

    // Apply search filter if provided
    let filteredLeads = leadsData;
    let filteredSubmissions = submissionsData;

    if (filters.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();

      filteredLeads = leadsData.filter(lead =>
        lead.contact_name.toLowerCase().includes(searchLower) ||
        lead.contact_email.toLowerCase().includes(searchLower) ||
        lead.contact_phone.toLowerCase().includes(searchLower)
      );

      filteredSubmissions = submissionsData.filter(submission =>
        submission.contact_name.toLowerCase().includes(searchLower) ||
        submission.contact_email.toLowerCase().includes(searchLower) ||
        submission.product_name.toLowerCase().includes(searchLower)
      );
    }

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add Leads sheet
    const leadsWs = XLSX.utils.json_to_sheet(filteredLeads);
    leadsWs['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 40 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, leadsWs, 'Leads');

    // Add Submissions sheet
    const submissionsWs = XLSX.utils.json_to_sheet(filteredSubmissions);
    submissionsWs['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 20 },
      { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 50 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, submissionsWs, 'Submissions');

    // Add Products sheet
    const productsWs = XLSX.utils.json_to_sheet(productsResult.data || []);
    XLSX.utils.book_append_sheet(wb, productsWs, 'Products');

    // Generate and download
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const dateStr = getDateString();
    downloadFile(blob, `tenant_comprehensive_report_${dateStr}.xlsx`);

    return {
      success: true,
      message: `Comprehensive report exported: ${filteredLeads.length} leads, ${filteredSubmissions.length} submissions!`
    };
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
